import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logoUrl from '../../src/styles/logo.png';
import { GoogleButton, LinkedInButton } from '../shared/GoogleButton';
import EmailCodeForm from '../shared/EmailCodeForm';
import { MAIN_ORIGIN } from '../../lib/domain';
import { handOffToApp } from '../../lib/authHandoff';

function BetaClosedView() {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={logoUrl} alt="RevertAI Logo" className="h-10 w-auto object-contain" />
          <span className="font-bold text-2xl tracking-tight text-white">
            Revert<span className="text-amber-500">AI</span>
          </span>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
            <span className="text-2xl">🔒</span>
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Accès sur invitation</h1>
          <p className="text-sm text-slate-400 mb-6">
            RevertAI est actuellement en bêta privée. Pour accéder à la plateforme, rejoignez la liste d'attente ou attendez votre invitation.
          </p>
          <a
            href={`${MAIN_ORIGIN}/#beta`}
            className="inline-flex items-center justify-center gap-2 w-full bg-amber-600 hover:bg-amber-500 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
          >
            Rejoindre la bêta <ArrowRight size={15} />
          </a>
          <p className="text-center text-sm text-slate-500 mt-5">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium transition-colors">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SignupPage() {
  const { signUp, signInWithGoogle, signInWithLinkedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') ?? undefined;
  // La page d'invitation transmet l'adresse invitée : s'inscrire avec une autre
  // ferait échouer la correspondance du jeton et créerait une organisation à
  // part, en silence.
  const invitedEmail = searchParams.get('email') ?? '';

  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!inviteToken) return <BetaClosedView />;

  // Aucun contrôle de domaine ni de nom d'organisation ici : on n'atteint cette
  // page qu'avec un jeton d'invitation, et c'est lui l'autorisation. Le
  // garde-fou « votre entreprise a déjà un espace RevertAI » bloquait
  // exactement les gens que cette entreprise venait d'inviter (test du
  // 2026-08-20) — il n'a de sens que sur une inscription spontanée, laquelle
  // renvoie sur BetaClosedView.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error } = await signUp(email, password, inviteToken);
    setLoading(false);
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={logoUrl} alt="RevertAI Logo" className="h-10 w-auto object-contain" />
          <span className="font-bold text-2xl tracking-tight text-white">
            Revert<span className="text-amber-500">AI</span>
          </span>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
          {success ? (
            // Le mail de confirmation porte un code ET un lien. Le lien se fait
            // brûler par les protections de messagerie d'entreprise avant que la
            // personne clique : on lui présente directement la saisie du code,
            // qui aboutit sans dépendre d'aucune URL.
            <div>
              <h2 className="text-lg font-semibold text-white mb-2">Confirmez votre adresse</h2>
              <p className="text-sm text-slate-400 mb-6">
                Un code vient d'être envoyé à <strong className="text-white">{email}</strong>.
                Saisissez-le pour terminer.
              </p>
              <EmailCodeForm
                initialEmail={email}
                lockEmail
                startAtVerify
                // L'onboarding réclame prénom et nom quand ils manquent — le cas
                // de tout invité — puis renvoie sur /home de lui-même.
                onSuccess={() => handOffToApp('/onboarding')}
              />
              <p className="text-center text-sm text-slate-500 mt-6">
                <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium transition-colors">
                  Retour à la connexion →
                </Link>
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-white mb-1">Créer un compte</h1>
              <p className="text-sm text-slate-400 mb-6">
                {inviteToken
                  ? "Créez votre compte pour rejoindre l'organisation"
                  : 'Rejoignez RevertAI pour gérer vos recrutements'}
              </p>

              <div className="space-y-2">
                <GoogleButton label="S'inscrire avec Google" onClick={() => signInWithGoogle(inviteToken)} />
                {/* TODO: Activer quand la connexion LinkedIn est implémentée
                <LinkedInButton label="S'inscrire avec LinkedIn" onClick={() => signInWithLinkedIn(inviteToken)} />
                */}
              </div>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-700" />
                <span className="text-xs text-slate-500">ou par email</span>
                <div className="flex-1 h-px bg-slate-700" />
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-red-950/50 border border-red-800 rounded-lg px-4 py-3 mb-5 text-red-400 text-sm">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>
                    {error}{' '}
                    {error.includes('existe déjà') && (
                      <Link to="/login" className="underline hover:text-red-300">Se connecter →</Link>
                    )}
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email de l’invitation, imposé quand la page d’invitation l’a transmis */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Email professionnel</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      readOnly={!!invitedEmail}
                      placeholder="vous@entreprise.com"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                    />
                  </div>
                  {invitedEmail && (
                    <p className="mt-1.5 text-xs text-slate-500">
                      L'invitation a été émise pour cette adresse.
                    </p>
                  )}
                </div>


                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Mot de passe</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="6 caractères minimum"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-amber-900 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm mt-2"
                >
                  {loading ? 'Création...' : 'Créer mon compte'}
                </button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                Déjà un compte ?{' '}
                <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium transition-colors">
                  Se connecter
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
